<script lang="ts">
import {defineComponent, type PropType} from 'vue';
import ComponentLibraryAccordionRow, {type RenderParamsProp} from './ComponentLibraryAccordionRow.vue';
import {LibraryComponentGroup, type ComponentLibraryFileType} from './library_component.ts';
import {onNextFrame} from '../../domutils.ts';

export default defineComponent({
  name: 'ComponentLibraryRowGroup',
  components: {
    ComponentLibraryAccordionRow,
  },
  props: {
    componentGroup: {
      type: LibraryComponentGroup,
      required: true,
    },
    nets: {
      type: Object, // Record<string, Net>
      required: true,
    },
    headerClassNames: {
      type: String,
      required: true,
    },
    isDiff: {
      type: Boolean,
      required: true,
    },
    fileType: {
      type: String as PropType<ComponentLibraryFileType>,
      required: true,
    },
    renderParams: {
      type: Object as PropType<RenderParamsProp>,
      required: false,
      default: null,
    },
    queryDocId: {
      type: String,
      required: false,
      default: '',
    },
    initialExpand: {
      type: Boolean,
      required: false,
      default: false,
    },
    folded: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  emits: ['collapseComponent'],

  data() {
    return {
      groupCurrentlyShown: this.componentGroup.isShown,
    };
  },

  mounted() {
    document.addEventListener('allspice-snippet-click-thru', this.handleSnippetClickThrough);
  },

  unmounted() {
    document.removeEventListener('allspice-snippet-click-thru', this.handleSnippetClickThrough);
  },

  methods: {
    handleSnippetClickThrough(event: Event) {
      if (this.groupCurrentlyShown) return;
      const docId = (event as CustomEvent).detail?.docId;
      if (!docId) return;
      const hasChild = this.componentGroup.components.some((c: {id: string}) => c.id === docId);
      if (hasChild) {
        this.expandGroup(true);
      }
    },

    /**
     * Show or hide this component group
     * @param {boolean} show
     */
    expandGroup(show: boolean) {
      const componentGroup = this.$refs.componentGroup as HTMLElement;
      this.groupCurrentlyShown = show;
      if (show) {
        componentGroup.style.display = 'block';
        componentGroup.style.maxHeight = `${componentGroup.scrollHeight}px`;
        setTimeout(() => {
          componentGroup.style.maxHeight = 'none';
        }, 300);
      } else {
        componentGroup.style.maxHeight = `${componentGroup.scrollHeight}px`;
        onNextFrame(() => {
          componentGroup.style.maxHeight = '0';
          setTimeout(() => {
            componentGroup.style.display = '';
          }, 300);
        });
      }
    },

    onCollapseComponent(componentId: string) {
      // Propagate to parent.
      this.$emit('collapseComponent', componentId);
    },
  },
});
</script>

<template>
  <div
    v-if="isDiff && groupCurrentlyShown && !componentGroup.isShown"
    style="padding: 0 !important"
  >
    <button
      class="ui button compact-toggle"
      title="Hide expanded unchanged components below"
      @click.prevent="expandGroup(false)"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        width="24"
        height="24"
      >
        <path d="M12 19a.749.749 0 0 1-.53-.22l-3.25-3.25a.749.749 0 0 1 .326-1.275.749.749 0 0 1 .734.215L12 17.19l2.72-2.72a.749.749 0 0 1 1.275.326.749.749 0 0 1-.215.734l-3.25 3.25A.749.749 0 0 1 12 19Z" />
        <path d="M12 18a.75.75 0 0 1-.75-.75v-7.5a.75.75 0 0 1 1.5 0v7.5A.75.75 0 0 1 12 18ZM2.75 6a.75.75 0 0 1 .75-.75h1a.75.75 0 0 1 0 1.5h-1A.75.75 0 0 1 2.75 6Zm4 0a.75.75 0 0 1 .75-.75h1a.75.75 0 0 1 0 1.5h-1A.75.75 0 0 1 6.75 6Zm4 0a.75.75 0 0 1 .75-.75h1a.75.75 0 0 1 0 1.5h-1a.75.75 0 0 1-.75-.75Zm4 0a.75.75 0 0 1 .75-.75h1a.75.75 0 0 1 0 1.5h-1a.75.75 0 0 1-.75-.75Zm4 0a.75.75 0 0 1 .75-.75h1a.75.75 0 0 1 0 1.5h-1a.75.75 0 0 1-.75-.75Z" />
      </svg>
    </button>
  </div>
  <div
    v-if="isDiff && !groupCurrentlyShown"
    style="padding: 0 !important"
  >
    <button
      class="ui button compact-toggle"
      title="Expand hidden unchanged components"
      @click.prevent="expandGroup(true)"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        width="24"
        height="24"
      >
        <path d="M12 23a.749.749 0 0 1-.53-.22l-3.25-3.25a.749.749 0 0 1 .326-1.275.749.749 0 0 1 .734.215L12 21.19l2.72-2.72a.749.749 0 0 1 1.275.326.749.749 0 0 1-.215.734l-3.25 3.25A.749.749 0 0 1 12 23Z" />
        <path d="M11.47 1.22a.75.75 0 0 1 1.06 0l3.25 3.25a.751.751 0 0 1-.018 1.042.751.751 0 0 1-1.042.018L12 2.81 9.28 5.53a.751.751 0 0 1-1.042-.018.751.751 0 0 1-.018-1.042ZM12 22.25a.75.75 0 0 1-.75-.75v-5.75a.75.75 0 0 1 1.5 0v5.75a.75.75 0 0 1-.75.75ZM2.75 12a.75.75 0 0 1 .75-.75h1a.75.75 0 0 1 0 1.5h-1a.75.75 0 0 1-.75-.75Zm4 0a.75.75 0 0 1 .75-.75h1a.75.75 0 0 1 0 1.5h-1a.75.75 0 0 1-.75-.75Zm4 0a.75.75 0 0 1 .75-.75h1a.75.75 0 0 1 0 1.5h-1a.75.75 0 0 1-.75-.75Zm4 0a.75.75 0 0 1 .75-.75h1a.75.75 0 0 1 0 1.5h-1a.75.75 0 0 1-.75-.75Zm4 0a.75.75 0 0 1 .75-.75h1a.75.75 0 0 1 0 1.5h-1a.75.75 0 0 1-.75-.75Z" />
        <path d="M12 1.5a.75.75 0 0 1 .75.75v6a.75.75 0 0 1-1.5 0v-6A.75.75 0 0 1 12 1.5Z" />
      </svg>
    </button>
  </div>
  <div
    ref="componentGroup"
    :class="!componentGroup.isShown ? 'component-group-hidden' : ''"
  >
    <component-library-accordion-row
      v-for="component in componentGroup.components"
      :key="component.id"
      :component="component"
      :nets="nets"
      :header-class-names="headerClassNames"
      :is-diff="isDiff"
      :is-shown="componentGroup.isShown"
      :file-type="fileType"
      :render-params="renderParams"
      :query-doc-id="queryDocId"
      :initial-expand="initialExpand"
      :folded="folded"
      @collapse-component="onCollapseComponent"
    />
  </div>
  <div
    v-if="isDiff && groupCurrentlyShown && !componentGroup.isShown"
    style="padding: 0 !important"
  >
    <button
      class="ui button compact-toggle"
      title="Hide expanded unchanged components above"
      @click.prevent="expandGroup(false)"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        width="24"
        height="24"
      >
        <path d="M11.47 5.22a.75.75 0 0 1 1.06 0l3.25 3.25a.751.751 0 0 1-.018 1.042.751.751 0 0 1-1.042.018L12 6.81 9.28 9.53a.751.751 0 0 1-1.042-.018.751.751 0 0 1-.018-1.042Z" />
        <path d="M12 5.5a.75.75 0 0 1 .75.75v8a.75.75 0 0 1-1.5 0v-8A.75.75 0 0 1 12 5.5ZM2.75 18a.75.75 0 0 1 .75-.75h1a.75.75 0 0 1 0 1.5h-1a.75.75 0 0 1-.75-.75Zm4 0a.75.75 0 0 1 .75-.75h1a.75.75 0 0 1 0 1.5h-1a.75.75 0 0 1-.75-.75Zm4 0a.75.75 0 0 1 .75-.75h1a.75.75 0 0 1 0 1.5h-1a.75.75 0 0 1-.75-.75Zm4 0a.75.75 0 0 1 .75-.75h1a.75.75 0 0 1 0 1.5h-1a.75.75 0 0 1-.75-.75Zm4 0a.75.75 0 0 1 .75-.75h1a.75.75 0 0 1 0 1.5h-1a.75.75 0 0 1-.75-.75Z" />
      </svg>
    </button>
  </div>
</template>

<style scoped>

.compact-toggle {
  width: 100%;
  padding: 5px !important;
  border-radius: 0 !important;
  border-right: none !important;
  border-left: none !important;
}

.component-group-hidden {
  max-height: 0;
  overflow: hidden;
  display: block;
  transition: max-height 0.3s ease-out;
}
</style>
